import React, {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Button } from "react-bootstrap";
import { ChevronDown, PencilFill } from "react-bootstrap-icons";
import { Link, useLocation } from "react-router-dom";
import {
  type Case,
  type Client,
  CommunicationSource,
  CommunicationStatus,
  type CredentialResult,
  DocumentStatus,
  DocumentSubType,
  DocumentType,
  Source,
} from "@/briefpoint-client";
import ConfirmCodeResetModal from "@/components/Modals/ConfirmCodeReset";
import LinkAndCodeModal from "@/components/Modals/LinkandCodeModal";
import ReminderEmailModal from "@/components/Modals/ReminderEmailModal";
import StartOverWarningModal from "@/components/Modals/StartOverWarning";
import { useCredentialApi } from "@/hooks/useApi";
import type { DocumentClient, ViewCommunicationResponse } from "@/lib/types";
import { formatShortDate, getDocumentSubTypeShortName } from "@/lib/utils";
import DocumentActionsMenu from "./DocumentActionsMenu";
import {
  DocumentIcon,
  DueSoon,
  FinishButton,
  formatLocalDate,
  NewComm,
  ReviewButton,
} from "./DocumentList";
import styles from "./DocumentRowWithReview.module.scss";

export function DocumentRowWithReview({
  doc,
  _case,
  client,
  archiveDocument,
  editDueDate,
  setShowSetCase,
  downloadForm,
  downloadRequest,
  createReview,
  communication,
  removeClientAccess,
  updateCommunication,
  updateDocumentName,
}: {
  doc: DocumentClient;
  _case?: Case;
  client?: Client;
  archiveDocument: (document: DocumentClient) => Promise<void>;
  editDueDate?: Dispatch<SetStateAction<DocumentClient | undefined>>;
  setShowSetCase?: Dispatch<SetStateAction<DocumentClient | undefined>>;
  downloadForm: (
    document: DocumentClient,
    formId: string,
    formTypeName: string,
  ) => Promise<void>;
  downloadRequest: (document: DocumentClient) => Promise<void>;
  createReview?: (document: DocumentClient) => void;
  communication?: ViewCommunicationResponse;
  removeClientAccess: (
    communication: ViewCommunicationResponse,
  ) => Promise<void>;
  updateCommunication: (
    communication: ViewCommunicationResponse,
  ) => Promise<void>;
  updateDocumentName: (newName: string) => Promise<void>;
}) {
  const showReminderAfterDays = 5;
  const [reviewExpanded, setReviewExpanded] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showCodeChangeModal, setShowCodeChangeModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedClient, setShowSelectedClient] = useState<Client>();
  const [showStartOverModal, setShowStartOverModal] = useState(false);
  const [showNewCodeSuccess, setShowNewCodeSuccess] = useState(false);
  const [isHoveringName, setIsHoveringName] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const pdfExtensionReplace = (name: string | undefined | null) =>
    name?.replace(".pdf", "").replace(".PDF", "") ?? "";
  const [docName, setDocName] = useState(pdfExtensionReplace(doc.name));
  const [oldDocName, setOldDocName] = useState(pdfExtensionReplace(doc.name));
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const highlightParam: string | null | undefined =
    searchParams.get("highlight");

  const inputRef = useRef<HTMLInputElement>(null);

  const handlePencilClick = () => {
    setIsEditingName(true);
    inputRef.current?.focus();
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      if (isEditingName) {
        inputRef.current?.blur();
      } else {
        setIsEditingName(true); //allow keyboard usage
      }
    }
    if (event.key === "Escape") {
      setDocName(oldDocName);
      setTimeout(() => {
        //add timeout so that the change can propagate
        inputRef.current?.blur();
      }, 10);
    }
  };

  const handleInputBlur = async () => {
    if (isEditingName) {
      setIsEditingName(false);
      if (docName !== oldDocName) {
        await updateDocumentName(docName);
        setOldDocName(docName);
      }
    }
  };

  const credentialApi = useCredentialApi();
  const [credential, setCredential] = useState<CredentialResult | undefined>();

  const loadCreds = useCallback(
    async (clientId: string) => {
      if (clientId) {
        const creds = await credentialApi.credentialCreate({
          clientId: clientId,
        });
        setCredential(creds);
      }
    },
    [credentialApi],
  );

  useEffect(() => {
    if (
      communication?.sharedWithObjectId &&
      (reviewExpanded || showEmailModal) &&
      !credential
    ) {
      loadCreds(communication?.sharedWithObjectId);
    }
  }, [
    communication?.sharedWithObjectId,
    credential,
    loadCreds,
    reviewExpanded,
    showEmailModal,
  ]);

  function getDaysPassed(timeSince: Date | undefined | null): number {
    if (!timeSince) {
      return -1;
    }
    const timeDiff = Math.abs(Date.now() - timeSince.getTime());
    const daysPassed = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return daysPassed;
  }

  function handleLinkAndCodeClose(showChangeCode: boolean) {
    setShowLinkModal(false);
    if (showChangeCode) {
      setShowCodeChangeModal(true);
    }
  }
  function handleCodeChangeClose(goBack: boolean) {
    setShowCodeChangeModal(false);
    if (goBack) {
      setShowLinkModal(true);
    }
  }

  async function handleConfirmCodeChange() {
    const clientId = client?.id ?? communication?.sharedWithObjectId;
    if (!clientId) {
      return;
    }
    setShowCodeChangeModal(false);
    setShowLinkModal(true);
    const newCreds = await credentialApi.credentialUpdate({
      clientId: clientId,
    });
    setCredential(newCreds);
    setShowNewCodeSuccess(true);
  }

  function sendReminder() {
    setShowEmailModal(true);
    setShowSelectedClient(client);
  }

  function sendReminderClose() {
    setShowEmailModal(false);
    setShowSelectedClient(undefined);
  }

  function getReviewText(
    communication: ViewCommunicationResponse | undefined,
  ): string {
    if (!communication) {
      return "";
    }

    const formatDate = (date: Date | null | undefined): string => {
      if (!date) {
        return "";
      }
      const month = date.getMonth() + 1;
      const day = date.getDate();
      return `${month}/${day}`;
    };

    switch (communication.status) {
      case CommunicationStatus.Sent:
        return `Review Requested ${formatDate(communication.lastActivityDate)}`;
      case CommunicationStatus.Open:
        return `Document Opened ${formatDate(communication.lastActivityDate)}`;
      case CommunicationStatus.InProgress:
        return `New Response ${formatDate(communication.lastActivityDate)}`;
      case CommunicationStatus.ChangesRequested:
        return `Changes Requested on ${formatDate(communication.lastActivityDate)}`;
      case CommunicationStatus.Complete:
        return `Review Completed ${formatDate(communication.lastActivityDate)}`;
      case CommunicationStatus.Pending:
        return `Draft Started ${formatDate(communication.lastActivityDate ?? communication.createdDate)}`;
      default:
        return "";
    }
  }

  const viewed = [
    CommunicationStatus.Open,
    CommunicationStatus.InProgress,
    CommunicationStatus.Complete,
    CommunicationStatus.ChangesRequested,
    CommunicationStatus.Closed,
  ];

  const dontShowDropDown = [
    CommunicationStatus.Open,
    CommunicationStatus.InProgress,
  ];

  const showReminderEmail = [
    CommunicationStatus.Sent,
    CommunicationStatus.InProgress,
    CommunicationStatus.Open,
  ];

  const timePassed = getDaysPassed(communication?.lastActivityDate);
  let showReviewOption = true;
  if (
    communication?.status &&
    dontShowDropDown.includes(communication?.status)
  ) {
    showReviewOption = false;
  }
  if (doc?.info?.documentSubType === DocumentSubType.Complaint) {
    showReviewOption = false;
  }

  const clientResponseNumber =
    communication?.externalCommunicationItems?.filter(
      (ci) => ci.source === CommunicationSource.Client,
    )?.length ?? 0;
  const canSendReminderEmail =
    (communication?.status &&
      showReminderEmail.includes(communication.status)) ??
    false;

  const removeBtnText =
    communication?.status === CommunicationStatus.Pending
      ? "Delete"
      : "Start Over";

  return (
    <React.Fragment>
      <ReminderEmailModal
        isShow={showEmailModal}
        onClose={sendReminderClose}
        clientId={communication?.sharedWithObjectId || undefined}
        selectedClient={selectedClient}
      />
      <tr
        className={highlightParam === doc.id ? styles.highlightRow : ""}
        style={reviewExpanded ? { borderBottom: "hidden" } : {}}
      >
        <td className="column-icon">
          {doc.source === Source.InternalGenerated ? (
            <DocumentIcon extension="doc" />
          ) : (
            <DocumentIcon extension="pdf" />
          )}
        </td>
        <td className="column-document-name name">
          <div
            style={{ display: "flex", justifyContent: "space-between" }}
            onMouseEnter={() => setIsHoveringName(true)}
            onMouseLeave={() => setIsHoveringName(false)}
          >
            <input
              ref={inputRef}
              value={docName}
              onChange={(e) => setDocName(e.target.value)}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              readOnly={!isEditingName}
              className="form-control-plaintext flex-grow-1 text-truncate"
            />
            {isHoveringName && (
              <PencilFill
                onClick={handlePencilClick}
                className="cursor-pointer"
                style={{ marginTop: "10px" }}
              />
            )}
          </div>
        </td>
        <td className="column-doc-type">
          {!!doc.info?.documentSubType &&
            getDocumentSubTypeShortName(
              doc.info.documentSubType,
              doc.info.jurisdiction ?? "",
            )}
        </td>
        <td className="column-case">
          {_case ? (
            <Link title={_case.shortTitle ?? ""} to={`/cases/${_case.id}`}>
              {_case.shortTitle}
            </Link>
          ) : (
            (doc.info?.caseNumber ?? "-")
          )}
        </td>

        <td className="column-upload-date">
          {formatShortDate(doc.uploadDate)}
        </td>
        {editDueDate && (
          <>
            <td className="column-due-date">
              {formatLocalDate(
                doc.info?.dueDateOverridden || doc.info?.dueDate || undefined,
              ) || (
                <Button
                  className="set-dueDate-button"
                  variant="link"
                  onClick={() => editDueDate(doc)}
                >
                  Set Due Date
                </Button>
              )}
            </td>
            <td className="column-due-soon">
              {DueSoon(
                doc.info?.dueDateOverridden || doc.info?.dueDate || undefined,
              )}
            </td>
          </>
        )}
        <td className="column-client">
          {" "}
          {client ? (
            <Link to={`/clients/${client.id}`}>{client.name ?? ""}</Link>
          ) : (
            ""
          )}
        </td>

        <td className={styles.columnClientActivity}>
          <div className={styles.clientActivityContainer}>
            <div className={styles.clientActivityHeaderRow}>
              {NewComm(communication, doc)}
              {getReviewText(communication)}{" "}
              {!!communication && (
                <ChevronDown
                  onClick={() => setReviewExpanded(!reviewExpanded)}
                />
              )}
            </div>
            {timePassed > showReminderAfterDays && canSendReminderEmail && (
              <div className={styles.activityReminderWrapper}>
                Last activity {timePassed} days ago.{" "}
                {/* biome-ignore lint/a11y/useKeyWithClickEvents: TODO: replace with button */}
                <span
                  className={styles.activityReminderTxt}
                  onClick={sendReminder}
                >
                  {" "}
                  Send client a reminder?{" "}
                </span>
              </div>
            )}
          </div>
        </td>
        <td className={styles.columnAction}>
          {doc.status === DocumentStatus.Complete ? (
            <ReviewButton
              documentId={doc.id}
              type={doc.info?.documentType ?? DocumentType.Unknown}
            />
          ) : (
            <FinishButton
              documentId={doc.id}
              type={doc.info?.documentType ?? DocumentType.Unknown}
            />
          )}
        </td>
        <td className="column-kebab kebab-cell">
          <DocumentActionsMenu
            client={client}
            document={doc}
            _case={_case}
            archiveDocument={archiveDocument}
            editDueDate={editDueDate}
            setShowSetCase={setShowSetCase}
            downloadForm={downloadForm}
            downloadRequest={downloadRequest}
            createReview={showReviewOption ? createReview : undefined}
            sendReminder={canSendReminderEmail ? sendReminder : undefined}
          />
        </td>
      </tr>
      {reviewExpanded && (
        <tr style={{ borderTop: "none" }}>
          <td colSpan={editDueDate ? 12 : 10}>
            <div className={styles.clientActivityRowContainer}>
              <div className={styles.clientActivityHeader}>
                Client Activity Details
                <hr className={styles.clientActivityHr} />
              </div>
              <div className={styles.clientActivityContent}>
                <table className={styles.clientActivityTable}>
                  <thead>
                    <tr>
                      <th className="client-activity-th">
                        Number of Questions
                      </th>
                      <th className="client-activity-th">Viewed By Client?</th>
                      <th className="client-activity-th">Client Answered</th>
                    </tr>
                    <tr className={styles.clientActivityTr}>
                      <td style={{ paddingTop: "0px" }}>
                        {
                          communication?.communicationInfo?.requestedReviewIds
                            ?.length
                        }
                      </td>
                      <td style={{ paddingTop: "0px" }}>
                        {communication?.status &&
                        viewed.includes(communication.status)
                          ? "Yes"
                          : "No"}
                      </td>
                      <td style={{ paddingTop: "0px" }}>
                        {clientResponseNumber}
                      </td>
                    </tr>
                  </thead>
                </table>
                <Button
                  className={styles.removeClientAccessBtn}
                  onClick={() => {
                    if (clientResponseNumber !== 0) {
                      setShowStartOverModal(true);
                    } else {
                      if (communication) {
                        removeClientAccess(communication);
                      }
                      setReviewExpanded(false);
                    }
                  }}
                >
                  {removeBtnText}
                </Button>
                {communication?.status === CommunicationStatus.Complete && (
                  <Button
                    className={styles.removeClientAccessBtn}
                    onClick={() => {
                      updateCommunication({
                        ...communication,
                        status: CommunicationStatus.Open,
                      });
                    }}
                  >
                    Re-open
                  </Button>
                )}
                <StartOverWarningModal
                  isShow={showStartOverModal}
                  onClose={(success: boolean) => {
                    if (success) {
                      if (communication) {
                        removeClientAccess(communication);
                      }
                      setReviewExpanded(false);
                    }
                    setShowStartOverModal(false);
                  }}
                />
                {communication?.status === CommunicationStatus.Pending ? (
                  <Button
                    className={styles.viewLinkCodeBtn}
                    onClick={() => {
                      if (createReview) {
                        createReview(doc);
                      }
                    }}
                  >
                    Continue
                  </Button>
                ) : (
                  <Button
                    className={styles.viewLinkCodeBtn}
                    onClick={() => {
                      setShowLinkModal(true);
                    }}
                  >
                    View Link and Code
                  </Button>
                )}
                <LinkAndCodeModal
                  isShow={showLinkModal}
                  onClose={handleLinkAndCodeClose}
                  credential={credential}
                  showNewCodeSuccess={showNewCodeSuccess}
                  client={client}
                />
                <ConfirmCodeResetModal
                  isShow={showCodeChangeModal}
                  onClose={handleCodeChangeClose}
                  onConfirm={handleConfirmCodeChange}
                />
              </div>
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}
